import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom"; 
import axios from "axios";
import "./AnticipationGame.css";

const AnticipationGame = () => {

  const navigate = useNavigate();

  const timerRef = useRef(null);
  const ballRef = useRef({ x: 0, y: 0 });
  const initialXRef = useRef(0);
  const testNumberRef = useRef(0);        // ✅ ADD
  const showLinesRef = useRef(false);     // ✅ ADD

  const [testNumber, setTestNumber] = useState(0);
  const [phase, setPhase] = useState("move");

  const [ball, setBall] = useState({ x: 0, y: 0 });
  const [visible, setVisible] = useState(true);
  const [showLines, setShowLines] = useState(false);

  const [mouseY, setMouseY] = useState(window.innerHeight / 2);

  const [startTime, setStartTime] = useState(0);
  const [testerStartTime, setTesterStartTime] = useState(0);

  const [result, setResult] = useState(null);
  const [allResults, setAllResults] = useState([]);

  const screenWidth = window.innerWidth;
  const screenHeight = window.innerHeight;

  const leftLine = 0.4 * screenWidth;
  const rightLine = 0.6 * screenWidth;

  const ballSize = Math.min(screenWidth, screenHeight) * 0.05;

  const linearConfigs = [
    { startY: 0.2, speedY: 1 },
    { startY: 0.2, speedY: 1 },
    { startY: 0.8, speedY: -1 },
    { startY: 0.8, speedY: -1 }
  ];

  const sineConfigs = [
    { amplitude: 0.12, waves: 1 },
    { amplitude: 0.15, waves: 1.5 },
    { amplitude: 0.18, waves: 2 },
    { amplitude: 0.22, waves: 2.5 },
    { amplitude: 0.25, waves: 3 }
  ];

  let speedX = testNumber % 2 === 0 ? 2 : -2;

  // ✅ CALIBRATION CHECK
  useEffect(() => {
    const pixel = Number(localStorage.getItem("pixel_in_mm"));

    if (!pixel || isNaN(pixel)) {
      navigate("/calibration", {
        state: { redirectTo: "/anticipation/play" }
      });
    }
  }, []);

  // ✅ ref sync + game start — single useEffect
  useEffect(() => {
    testNumberRef.current = testNumber;
    startGame();
    return () => clearTimeout(timerRef.current);
  }, [testNumber]);

  const startGame = () => {

    clearTimeout(timerRef.current);

    showLinesRef.current = false;   // ✅ reset ref
    setShowLines(false);

    const tn = testNumberRef.current;

    let startX = tn % 2 === 0 ? 50 : screenWidth - 50;
    let startY;

    if (tn < 4) {
      startY = screenHeight * linearConfigs[tn].startY;
    } else {
      startY = screenHeight / 2;
    }

    initialXRef.current = startX;

    ballRef.current = { x: startX, y: startY };
    setBall(ballRef.current);

    setVisible(true);
    setPhase("move");

    setStartTime(performance.now());

    move();
  };

  const move = () => {

    const tn = testNumberRef.current;   // ✅ always fresh
    const speedX = tn % 2 === 0 ? 2 : -2;

    let { x, y } = ballRef.current;

    const shouldMove = tn % 2 === 0
      ? (x + ballSize) < leftLine
      : x > rightLine;

    if (!shouldMove) {
      ballHitLine();
      return;
    }

    x += speedX;

    if (tn < 4) {
      y += linearConfigs[tn].speedY;
    } else {
      const config = sineConfigs[tn - 4];
      const progress = tn % 2 === 0
        ? (x - 50) / (leftLine - 50)
        : (screenWidth - 50 - x) / (screenWidth - 50 - rightLine);
      const clamped = Math.max(0, Math.min(1, progress));
      const amplitude = screenHeight * config.amplitude;
      y = screenHeight / 2 + amplitude * Math.sin(clamped * Math.PI * 2 * config.waves);
    }

    ballRef.current = { x, y };
    setBall({ x, y });

    // ✅ ref use பண்ணி ஒரே ஒரு தடவை set
    if (!showLinesRef.current) {
      if (tn % 2 === 0) {
        if (x > 0.7 * leftLine) {
          showLinesRef.current = true;
          setShowLines(true);
          console.log("showLines SET TRUE", x, leftLine); // ✅ ADD
        }
      } else {
        if (x < rightLine + 0.3 * (screenWidth - rightLine)) {
          showLinesRef.current = true;
          setShowLines(true);
          console.log("showLines SET TRUE", x, rightLine); // ✅ ADD
        }
      }
    }

    timerRef.current = setTimeout(move, 20);
  };

  const ballHitLine = () => {
    clearTimeout(timerRef.current);
    setVisible(false);
    setPhase("predict");
    setTesterStartTime(performance.now());
  };

  const estimateY = (futureX) => {

    if (testNumber < 4) {
      const { x, y } = ballRef.current;
      return y + linearConfigs[testNumber].speedY *
        ((futureX - x) / speedX);
    } else {
      const config = sineConfigs[testNumber - 4];

      const progress =
        testNumber % 2 === 0
          ? (futureX - 50) / (leftLine - 50)
          : (screenWidth - 50 - futureX) / (screenWidth - 50 - rightLine);

      const clamped = Math.max(0, Math.min(1, progress));
      const amplitude = screenHeight * config.amplitude;

      return (
        screenHeight / 2 +
        amplitude *
        Math.sin(clamped * Math.PI * 2 * config.waves)
      );
    }
  };

  const handleClick = () => {

    if (phase !== "predict") return;

    const testerEnd = performance.now();
    const testerTime = (testerEnd - testerStartTime) / 1000;

    const futureX =
      testNumber % 2 === 0 ? rightLine : leftLine;

    const expectedY = estimateY(futureX);

    const pixels_in_mm = Number(localStorage.getItem("pixel_in_mm"));
    const dpi = pixels_in_mm * 25.4;

    // ✅ SAFE CALCULATION
    let diffCm = 0;
    if (dpi > 0) {
      const expectedCenterY = expectedY + ballSize / 2;
      const diffPx = mouseY - expectedCenterY;
      diffCm = (diffPx * 2.54) / dpi;
    }

    const totalTime = (performance.now() - startTime) / 1000;

    const distanceCovered =
      testNumber % 2 === 0
        ? leftLine - initialXRef.current
        : initialXRef.current - rightLine;

    let expectedTime = 0;
    if (distanceCovered !== 0) {
      expectedTime =
        ( (rightLine - leftLine) * totalTime ) / distanceCovered;
    }

    const timeError = testerTime - expectedTime;

    // ✅ FINAL SAFE VALUES
    const safeDistance = isNaN(diffCm) ? 0 : Number(diffCm.toFixed(2));
    const safeTime = isNaN(timeError) ? 0 : Number(timeError.toFixed(2));

    const finalData = {
      testNumber: testNumber + 1,
      GameName: testNumber < 4 ? "Linear" : "Sine",

      // 🔥 BACKEND MATCH
      DistanceError: `${safeDistance}cm`,
      TimeError: safeTime,

      ExpectedBallY: expectedY,
      userClickBallY: mouseY
    };

    setAllResults(prev => [...prev, finalData]);

    setBall({ x: futureX, y: expectedY });
    setVisible(true);
    setResult({
      test: testNumber + 1,
      distanceError: safeDistance,
      timeError: safeTime
    });

    setPhase("result");
  };

  const next = () => {
    setResult(null);
    setMouseY(screenHeight / 2);
    setTestNumber(prev => prev + 1);
  };

  const finish = async () => {

    const email = localStorage.getItem("rg_email");

    try {
      const res = await axios.post("/save-anticipation", {
        email,
        results: allResults
      });

      navigate("/anticipation/result", {
        state: { results: allResults, score: res.data.score }
      });

    } catch {
      navigate("/anticipation/result", {
        state: { results: allResults }
      });
    }
  };

  return (
    <div
      className="anticipation-container"
      onMouseMove={(e) => {
        if (phase === "predict") {
          requestAnimationFrame(() => {
            setMouseY(e.clientY);
          });
        }
      }}
      onClick={handleClick}
      style={{
        cursor: phase === "predict" ? "none" : "default"
      }}
    >

      {/* BALL */}
      {visible && (
        <div
          className="ball"
          style={{
            left: ball.x,
            top: ball.y,
            width: ballSize,
            height: ballSize
          }}
        />
      )}

      {/* LINES */}
      {showLines && (
        <>
          <div className="line" style={{ left: leftLine }} />
          <div className="line" style={{ left: rightLine }} />
        </>
      )}

      {/* ARROW */}
      {phase === "predict" && (
        <div
          className="arrow"
          style={{
            top: mouseY,
            left: testNumber % 2 === 0 ? rightLine : leftLine
          }}
        />
      )}

      {/* RESULT */}
      {phase === "result" && result && (
        <div className="result-box">
          <h2>Test {result.test}</h2>
          <p>{result.distanceError} cm</p>
          <p>
            {Math.abs(result.timeError)}s{" "}
            {result.timeError > 0 ? "slow" : "fast"}
          </p>

          {testNumber === 8 ? (
            <button onClick={finish}>Finish</button>
          ) : (
            <button onClick={next}>Next</button>
          )}
        </div>
      )}

    </div>
  );
};

export default AnticipationGame;